/**
 * Knowledge Canvas detail — ``/workspace/canvases/$canvasId``.
 *
 * May 25, 2026 — opened from ``CanvasCard`` (hub list). Reads the canvas
 * by id from the BE (single-query polled at 5s while compiling, 30s
 * otherwise) and renders the compiled markdown body inside the existing
 * v2 ``SurfaceLayout`` shell so the chrome matches every other workspace
 * surface (conversations, documents, automations).
 *
 * Why server-rendered markdown via ``MessageBody`` (already in @ando/ui):
 *   - Single rendering primitive across chat answers and canvases — same
 *     citation chip styling, same code-block treatment, same prose width.
 *   - Avoids a second markdown dep + a divergent citation-anchor format.
 *
 * Actions surfaced in the header (one-shot, idempotent):
 *   - Rigenera → ``POST /canvases/{id}/regenerate`` (forces re-compile)
 *   - Archivia / Ripristina → ``POST/DELETE /canvases/{id}/archive``
 *
 * Out of scope for v2 phase 1 (mirrors v1 ``CanvasDetailPage`` later):
 *   - Compile run history accordion (BE: ``GET /compile-runs?limit=N``)
 *   - Section TOC sidebar (BE field ``sections[]`` is wired through; we
 *     just don't render the navigator yet)
 *   - Citation drawer linking (``citation_map``) — needs the SourceDrawer
 *     wiring next sprint.
 *
 * Capability gate: same as the hub — ``useHasCapability("canvases")``.
 * If the user lands here without entitlement (deep link from email) we
 * show the same locked card the hub uses.
 */

import type * as React from "react";
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  Archive,
  ArchiveRestore,
  BookOpenText,
  Loader2,
  RefreshCw,
  Lock,
  AlertTriangle,
} from "lucide-react";
import {
  SurfaceEmptyState,
  SurfaceHeader,
  SurfaceLayout,
  cn,
} from "@ando/ui";
import { AndoBrainLoader } from "@ando/ui/blob";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

import { useActiveContextScope } from "@/features/active-context";
import { useHasCapability, useCapability } from "@/features/capabilities";
import {
  CanvasReader,
  CanvasStatusChip,
  useCanvas,
  useArchiveCanvas,
  useUnarchiveCanvas,
  useRegenerateCanvas,
} from "@/features/canvases";

export const Route = createFileRoute(
  "/_authed/workspace/canvases/$canvasId",
)({
  component: CanvasDetailRoute,
});

function CanvasDetailRoute() {
  const { i18n } = useLingui();
  const { canvasId } = useParams({
    from: "/_authed/workspace/canvases/$canvasId",
  });
  const navigate = useNavigate();
  const hasAccess = useHasCapability("canvases");
  const { state: capState } = useCapability("canvases");
  const { canvas, isLoading, isError, refetch } = useCanvas(canvasId, {
    enabled: hasAccess,
  });

  const regen = useRegenerateCanvas();
  const archive = useArchiveCanvas();
  const unarchive = useUnarchiveCanvas();

  const isArchived = canvas?.compile_status === "archived";
  const isCompiling = canvas?.compile_status === "compiling";
  const isFailed = canvas?.compile_status === "failed";

  // Active context — surface the open canvas to the chat agent so
  // "summarise this canvas", "what sources does it cover?", "compare
  // it to the Q3 one" land on THIS canvas without name-dropping.
  useActiveContextScope(
    canvas
      ? {
          kind: "canvas",
          id: canvas.id,
          title: canvas.title,
          summary: [canvas.entity_type, canvas.scope]
            .filter(Boolean)
            .join(" · ") || undefined,
          meta: {
            entity_type: canvas.entity_type,
            scope: canvas.scope,
            ...(typeof canvas.source_count === "number"
              ? { source_count: canvas.source_count }
              : {}),
            ...(canvas.compile_status
              ? { compile_status: canvas.compile_status }
              : {}),
          },
        }
      : null,
  );

  // ── Capability still resolving ────────────────────────────────────
  if (capState === "loading") {
    return (
      <SurfaceLayout
        header={
          <SurfaceHeader
            label="Canvases"
            title="Knowledge Canvas"
            meta={<span className="text-ink-3"><Trans>Caricamento…</Trans></span>}
          />
        }
      >
        <div />
      </SurfaceLayout>
    );
  }

  if (!hasAccess) {
    return (
      <SurfaceLayout
        header={<SurfaceHeader label="Canvases" title="Knowledge Canvas" />}
      >
        <SurfaceEmptyState
          icon={<Lock size={18} strokeWidth={1.75} />}
          title={i18n._(msg`Canvases non attivi sul tuo piano`)}
          description={i18n._(msg`I Knowledge Canvases sono inclusi nei piani Knowledge e superiori. Contatta il tuo amministratore per attivarli.`)}
        />
      </SurfaceLayout>
    );
  }

  // ── Loading initial fetch ─────────────────────────────────────────
  if (isLoading || !canvas) {
    if (isError) {
      return (
        <SurfaceLayout
          header={
            <SurfaceHeader
              label="Canvases"
              title={i18n._(msg`Canvas non trovato`)}
              meta={<BackToHubLink />}
            />
          }
        >
          <SurfaceEmptyState
            icon={<AlertTriangle size={18} strokeWidth={1.75} />}
            title={i18n._(msg`Impossibile aprire questo canvas`)}
            description={i18n._(msg`Potrebbe essere stato eliminato o non hai più accesso. Torna alla lista o riprova.`)}
            action={
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void refetch()}
                  className="hover:-translate-y-px rounded-md bg-ink-2 px-3 py-1.5 text-[12px] text-white transition-transform"
                >
                  <Trans>Riprova</Trans>
                </button>
                <Link
                  to="/workspace/canvases"
                  className="rounded-md border border-hairline bg-transparent px-3 py-1.5 text-[12px] text-ink-1 transition-colors hover:bg-cream"
                >
                  <Trans>Torna ai canvas</Trans>
                </Link>
              </div>
            }
          />
        </SurfaceLayout>
      );
    }
    return (
      <SurfaceLayout
        header={
          <SurfaceHeader
            label="Canvases"
            title={i18n._(msg`Caricamento canvas…`)}
            meta={<BackToHubLink />}
          />
        }
      >
        <div className="mx-8 my-6">
          <AndoBrainLoader size="md" label={i18n._(msg`Stiamo aprendo il canvas…`)} />
        </div>
      </SurfaceLayout>
    );
  }

  // ── Action handlers ──────────────────────────────────────────────
  const handleRegenerate = (): void => {
    regen.mutate(
      { id: canvas.id, force: true },
      {
        onSuccess: () => {
          void refetch();
        },
      },
    );
  };

  const handleToggleArchive = (): void => {
    if (isArchived) {
      unarchive.mutate(canvas.id, {
        onSuccess: () => void refetch(),
      });
    } else {
      archive.mutate(canvas.id, {
        onSuccess: () => {
          // Archived canvases vanish from the active list; bounce back
          // so the user sees a coherent post-action state instead of
          // staring at a row that's about to disappear.
          void navigate({
            to: "/workspace/canvases",
            search: (prev) => prev,
          });
        },
      });
    }
  };

  const isBusy =
    regen.isPending || archive.isPending || unarchive.isPending || isCompiling;

  return (
    <SurfaceLayout
      header={
        <SurfaceHeader
          label="Canvases"
          title={canvas.title}
          meta={
            <span className="inline-flex items-center gap-2 text-[11px] text-ink-3 uppercase tracking-[0.05em]">
              <BackToHubLink />
              <span aria-hidden>·</span>
              <span>{canvas.entity_type}</span>
              <span aria-hidden>·</span>
              <span>{canvas.scope}</span>
              {canvas.source_count > 0 ? (
                <>
                  <span aria-hidden>·</span>
                  <span>
                    {canvas.source_count === 1
                      ? i18n._(msg`${canvas.source_count} fonte`)
                      : i18n._(msg`${canvas.source_count} fonti`)}
                  </span>
                </>
              ) : null}
            </span>
          }
        />
      }
      toolbar={
        <div className="mx-8 my-4 flex flex-wrap items-center gap-2">
          <CanvasStatusChip status={canvas.compile_status} />

          {/* Primary action — Rigenera is the canvas-defining gesture
              (user wants a fresh take). Visually anchored as the warm
              accent so it stands out from the secondary archive
              control even at a glance. */}
          <button
            type="button"
            onClick={handleRegenerate}
            disabled={isBusy}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md",
              "bg-ando-orange px-2.5 py-1.5 font-medium text-[11.5px] text-white",
              "shadow-xs transition-all",
              "hover:-translate-y-px hover:bg-ando-orange-light hover:shadow-sm",
              "active:translate-y-0",
              "disabled:cursor-not-allowed disabled:opacity-60",
              "disabled:hover:translate-y-0 disabled:hover:shadow-xs",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-ando-orange focus-visible:ring-offset-2",
              "focus-visible:ring-offset-cream",
            )}
            aria-label={i18n._(msg`Rigenera il canvas`)}
          >
            {regen.isPending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <RefreshCw size={13} strokeWidth={1.75} />
            )}
            {regen.isPending ? i18n._(msg`Rigenero…`) : i18n._(msg`Rigenera`)}
          </button>

          {/* Secondary action — Archivia is reversible but moves the
              row off the active hub; ghost styling keeps it discoverable
              without competing with Rigenera for the eye. */}
          <button
            type="button"
            onClick={handleToggleArchive}
            disabled={isBusy}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border border-hairline",
              "bg-transparent px-2.5 py-1.5 font-medium text-[11.5px] text-ink-1",
              "transition-colors hover:bg-cream",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-ando-orange focus-visible:ring-offset-2",
              "focus-visible:ring-offset-cream",
            )}
            aria-label={isArchived ? i18n._(msg`Ripristina dal cestino`) : i18n._(msg`Archivia canvas`)}
          >
            {archive.isPending || unarchive.isPending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : isArchived ? (
              <ArchiveRestore size={13} strokeWidth={1.75} />
            ) : (
              <Archive size={13} strokeWidth={1.75} />
            )}
            {isArchived ? i18n._(msg`Ripristina`) : i18n._(msg`Archivia`)}
          </button>
        </div>
      }
    >
      {/* Reader column — paper-like card on the cream shell. Keeps the
          long-form content visually distinct from the workspace chrome
          (hub list, toolbar) without competing with the focused detail.
          Width steps up with the viewport so widescreen monitors don't
          waste half the canvas on whitespace — capped at 6xl (1152px)
          to preserve the 75-90ch reading-comfort ceiling. Below md the
          ``max-w-prose`` legacy (~65ch) stays in charge: phones/tablets
          read narrower bodies better, full stop. */}
      <div className="mx-auto w-full max-w-prose px-4 pt-2 pb-10 md:max-w-3xl md:px-8 lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl">
        <article
          className={cn(
            "rounded-2xl border border-hairline bg-white/70 px-6 py-7 md:px-10 md:py-9",
            // Two-step shadow → 1px contact + 12px soft halo. Reads as
            // "paper sitting on the cream surface" without competing
            // with the focused detail rail; ``ring-1`` keeps a hairline
            // at every zoom level so the card edge never disappears
            // against ``cream-deep``.
            "shadow-[0_1px_2px_rgba(0,0,0,0.04),0_12px_28px_-16px_rgba(0,0,0,0.10)]",
            "ring-1 ring-black/[0.03]",
          )}
        >
          {canvas.subtitle ? (
            <p className="mb-5 border-ando-orange-soft border-l-2 pl-3 text-[13.5px] text-ink-2 italic leading-relaxed">
              {canvas.subtitle}
            </p>
          ) : null}

          {isFailed && canvas.last_compile_error ? (
            <div className="mb-6 rounded-lg border border-red-200/60 bg-red-50/70 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle
                  size={14}
                  className="mt-0.5 shrink-0 text-red-600"
                  strokeWidth={1.75}
                />
                <div className="min-w-0">
                  <p className="font-medium text-[12.5px] text-red-700">
                    <Trans>Generazione non riuscita</Trans>
                  </p>
                  <p className="mt-0.5 text-[12px] text-red-600/90">
                    {canvas.last_compile_error}
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          {isCompiling ? (
            <div className="mb-6 flex items-center gap-2 rounded-lg border border-black/5 bg-white/50 px-3 py-2.5 text-[12px] text-ink-2">
              <Loader2 size={14} className="animate-spin" />
              <span>
                <Trans>Sto componendo il canvas — di solito ci vogliono 30-60 secondi.</Trans>
              </span>
            </div>
          ) : null}

          {canvas.content_md && canvas.content_md.trim().length > 0 ? (
            <CanvasReader markdown={canvas.content_md} />
          ) : !isCompiling ? (
            <SurfaceEmptyState
              icon={<BookOpenText size={18} strokeWidth={1.75} />}
              title={i18n._(msg`Canvas ancora vuoto`)}
              description={i18n._(msg`Premi Rigenera per ricomporre il contenuto a partire dalle ultime fonti disponibili.`)}
            />
          ) : null}
        </article>
      </div>
    </SurfaceLayout>
  );
}

function BackToHubLink(): React.ReactNode {
  return (
    <Link
      to="/workspace/canvases"
      className="inline-flex items-center gap-1 text-[11px] text-ink-3 transition-colors hover:text-ink-1"
    >
      <ArrowLeft size={12} strokeWidth={1.75} />
      <Trans>Tutti i canvas</Trans>
    </Link>
  );
}
